import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Typography,
  Divider,
  Stack,
  CircularProgress,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { abyssColors } from '@/theme/colors';
import { ReportInternalType, ReportStatus } from '@/__generated__/graphql';
import { ReportsList } from './ReportsList';
import { useFilteredReportsByType } from '../hooks/useFilteredReportsByType';

type ReportGroup = {
  groupId: string;
  groupName: string;
  count: number;
};

type ReportGroupsProps = {
  reportGroup: ReportGroup;
  selectedReportType: ReportInternalType;
  status: ReportStatus | 'ALL';
  searchTerm: string | undefined;
  selectedReportIds: string[];
  setSelectedReportIds: React.Dispatch<React.SetStateAction<string[]>>;
};

export const ReportGroups = ({
  reportGroup,
  selectedReportType,
  status,
  searchTerm,
  selectedReportIds,
  setSelectedReportIds,
}: ReportGroupsProps) => {
  const { reports , loading } = useFilteredReportsByType(
    selectedReportType,
    status,
    searchTerm,
    reportGroup.groupId,
    reportGroup.groupName
  );

  return (
    <>
      <Accordion
        disableGutters
        elevation={0}
        sx={{
          boxShadow: 'none',
          pt: 1,
          pb: 1,
          '&:before': { display: 'none' },
        }}
      >
        <AccordionSummary
          aria-controls={`${reportGroup.groupId}-content`}
          id={`${reportGroup.groupId}-header`}
          expandIcon={<ExpandMoreIcon />}
          sx={{
            flexDirection: 'row-reverse',
            px: 0,
            py: 1,
            '& .MuiAccordionSummary-expandIconWrapper': {
              transform: 'rotate(-90deg)',
              transition: 'transform 0.2s ease-in-out',
              '&.Mui-expanded': {
                transform: 'rotate(0deg)',
              },
              marginRight: 1,
            },
            '& .MuiSvgIcon-root': {
              color: abyssColors.primary[500],
            },
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center" width="100%">
            <Typography
              variant="h5"
              sx={{ color: abyssColors.primary[700], fontWeight: 500, fontSize: '1.4rem', flex: 1 }}
            >
              {reportGroup.groupName}
            </Typography>
            <Typography
              variant="h4"
              sx={{ color: abyssColors.primary[700], fontWeight: 500, fontSize: '2.0rem' }}
            >
              {loading ? <CircularProgress size={20} /> : String(reports.length)}
            </Typography>
          </Stack>
        </AccordionSummary>

        <AccordionDetails sx={{ px: 0, pt: 0 }}>
          <ReportsList
            selectedReportType={selectedReportType}
            status={status}
            searchTerm={searchTerm}
            groupId={reportGroup.groupId}
            groupName={reportGroup.groupName}
            selectedReportIds={selectedReportIds}
            setSelectedReportIds={setSelectedReportIds}
          />
        </AccordionDetails>
      </Accordion>
      <Divider />
    </>
  );
};
